import { forEach, map } from "lodash";

import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { Precision } from "../../types";
import isImageUrl from "../../utils/isImageUrl";
import uniqueId from "../../utils/uniqueId";
import { updateStartAndDuration } from "./updateDates";
import upgradeAttachment from "./upgradeAttachment";
import upgradeImage from "./upgradeImage";
import upgradeLink from "./upgradeLink";
import upgradeRelationship from "./upgradeRelationship";

const propertyValueForFormat = (v, format) => {
  let value = null;
  switch (format) {
    case PropertyFormatIdentifier.integer:
    case PropertyFormatIdentifier.double:
    case PropertyFormatIdentifier.percentage:
    case PropertyFormatIdentifier.currency:
      value = Number(v);
      break;
    case PropertyFormatIdentifier.boolean:
      value = v === true || Boolean(Number(v));
      break;
    default:
      value = String(v);
  }
  return value;
};

/**
 * This is not a pure function: tags, inverseTagLookup, entitiesById, relationships, and attachments are all mutated
 * @param {object} v2Event
 * @param {number} index
 * @param {object} definitions
 * @param {string} typeId
 * @param {string} eventSummaryProperty
 * @param {object} tags
 * @param {object} inverseTagLookup
 * @param {array} relationships
 * @param {object} entitiesById
 * @param {array} attachments
 * @param {object} newReferenceIdForV2Id
 * @param {array} dependencies
 * @param {array} dateConstraints
 * @param {boolean} updateDatesInBC
 * @param {CalendarRecord} calendarRecord
 */
const upgradeEvent = (
  v2Event,
  index,
  definitions,
  typeId,
  eventSummaryProperty,
  tags,
  inverseTagLookup,
  relationships,
  entitiesById,
  attachments,
  newPropIdForV2Id,
  newReferenceIdForV2Id,
  dependencies,
  dateConstraints,
  updateDatesInBC,
  calendarRecord
) => {
  const type = definitions.types.byId[typeId];
  const prefix = type.identifierPrefix;
  let summary = "";
  let propertyValues = {};
  forEach(v2Event.values, v => {
    if (eventSummaryProperty && eventSummaryProperty.id === v.property) {
      summary = v.value;
    } else {
      const propId = newPropIdForV2Id[v.property] || v.property;
      const finalProp = definitions.properties.byId[propId];
      propertyValues[propId] = propertyValueForFormat(v.value, finalProp.format);
    }
  });

  let v2Start = {
    precision: Precision.fromString(v2Event.rangeValues[0].position.precision),
    timestamp: v2Event.rangeValues[0].position.timestamp
  };
  let v2Span = v2Event.rangeValues[0].span;

  const { startDate: startDateRec, duration: durationRec } = updateStartAndDuration(
    calendarRecord,
    updateDatesInBC,
    v2Start,
    v2Span
  );
  const startDate = startDateRec.toObject();
  const duration = durationRec.toObject();

  const eventTags = map(v2Event.tags, str => {
    let tagId = inverseTagLookup[str];
    if (!tagId) {
      tagId = uniqueId();
      inverseTagLookup[str] = tagId;
      tags[tagId] = str;
    }
    return tagId;
  });

  let event = {
    id: v2Event.guid,
    type: typeId,
    ongoing: false,
    super: v2Event.parent,
    label: v2Event.title,
    color: v2Event.color,
    displayId: prefix + index,
    summary,
    tags: eventTags,
    startDate,
    preferredStartDate: startDate,
    duration,
    preferredDuration: duration,
    references: [],
    inverseReferences: [],
    attachments: [],
    links: [],
    propertyValues,
    image: null,
    dateConstraints: [],
    blockingDependencies: [],
    blockedByDependencies: []
  };

  forEach(v2Event.relationships, r => {
    const relationship = upgradeRelationship(r, event.id, newReferenceIdForV2Id);
    relationships.push(relationship);
    entitiesById[relationship.object].inverseReferences.push(relationship.id);
    event.references.push(relationship.id);
  });

  // Process attachments and image
  forEach(v2Event.attachments, a => {
    const attachment = upgradeAttachment(a);
    attachments.push(attachment);
    event.attachments.push(attachment.id);
  });

  // Process image
  if (v2Event.image && v2Event.image.guid) {
    const attachment = upgradeImage(v2Event.image);
    attachments.push(attachment);
    event.image = attachment.id;
  }

  // Process links
  forEach(v2Event.links, l => {
    const attachment = upgradeLink(l);
    if (!event.image && isImageUrl(attachment.path)) {
      event.image = attachment.id;
    } else {
      event.links.push(attachment.id);
    }
    attachments.push(attachment);
  });

  forEach(dependencies, d => {
    if (d.subject === event.id) {
      event.blockingDependencies.push(d.id);
    } else if (d.object === event.id) {
      event.blockedByDependencies.push(d.id);
    }
  });

  forEach(dateConstraints, c => {
    if (c.subject === event.id || c.object === event.id) {
      event.dateConstraints.push(c.id);
    }
  });
  return event;
};

export default upgradeEvent;
